import type { UseFormReturn } from 'react-hook-form';
import { Link } from 'react-router-dom';
import type { UseMutationResult } from '@tanstack/react-query';
import {
    ArrowLeft,
    EyeIcon,
    EyeOffIcon,
    LockKeyhole,
    Mail,
} from 'lucide-react';

import { Stepper } from './Stepper';

import { Button } from '@/components/ui/button';
import {
    Card,
    CardContent,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

interface Step1Data {
    email: string;
    password: string;
    confirmPassword: string;
}

interface SignupStep1Props {
    form: UseFormReturn<Step1Data>;
    onSubmit: (data: Step1Data) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    emailCheckMutation: UseMutationResult<any, Error, string, unknown>;
    isPasswordVisible: boolean;
    setIsPasswordVisible: (v: boolean) => void;
    isConfirmVisible: boolean;
    setIsConfirmVisible: (v: boolean) => void;
}

export function SignupStep1({
    form,
    onSubmit,
    emailCheckMutation,
    isPasswordVisible,
    setIsPasswordVisible,
    isConfirmVisible,
    setIsConfirmVisible,
}: SignupStep1Props) {
    return (
        <div className="min-h-screen bg-background flex flex-col">
            <div className="p-6">
                <Link
                    to="/login"
                    className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                    <ArrowLeft className="h-4 w-4" />
                    Login
                </Link>
            </div>

            <div className="flex-1 flex items-center justify-center px-4 pb-12">
                <div className="flex flex-col gap-6 w-full max-w-sm">
                    <div className="flex flex-col items-center gap-3">
                        <Link to="/"><img src="/chanco-vertical.png" alt="Chanco" className="h-24 w-auto" /></Link>
                        <h1 className="text-2xl font-bold text-foreground">Signup</h1>
                        <p className="text-sm text-muted-foreground">Please enter your details to register</p>
                    </div>

                    <Stepper activeStep={1} />

                    <Card className="border-0 shadow-lg">
                        <CardContent className="pt-6">
                            <form onSubmit={form.handleSubmit(onSubmit)}>
                                <div className="grid gap-5">
                                    <div className="grid gap-2">
                                        <Label htmlFor="email">Email</Label>
                                        <div className="relative">
                                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                            <Input id="email" type="text" placeholder="m@example.com" {...form.register('email')} className={cn('pl-10', { 'focus-visible:ring-destructive': form.formState.errors.email })} />
                                        </div>
                                        {form.formState.errors.email && <span className="text-sm text-destructive">{form.formState.errors.email.message}</span>}
                                    </div>

                                    <div className="grid gap-2">
                                        <Label htmlFor="password">Password</Label>
                                        <div className="relative">
                                            <LockKeyhole className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                            <Input id="password" type={isPasswordVisible ? 'text' : 'password'} placeholder="Enter your password" {...form.register('password')} className={cn('pl-10 pr-10', { 'focus-visible:ring-destructive': form.formState.errors.password })} />
                                            <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground focus:outline-none" onClick={() => setIsPasswordVisible(!isPasswordVisible)} aria-label={isPasswordVisible ? 'Hide password' : 'Show password'}>
                                                {isPasswordVisible ? <EyeIcon className="h-4 w-4" /> : <EyeOffIcon className="h-4 w-4" />}
                                            </button>
                                        </div>
                                        {form.formState.errors.password && <span className="text-sm text-destructive">{form.formState.errors.password.message}</span>}
                                    </div>

                                    <div className="grid gap-2">
                                        <Label htmlFor="confirmPassword">Confirm Password</Label>
                                        <div className="relative">
                                            <LockKeyhole className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                            <Input id="confirmPassword" type={isConfirmVisible ? 'text' : 'password'} placeholder="Confirm your password" {...form.register('confirmPassword')} className={cn('pl-10 pr-10', { 'focus-visible:ring-destructive': form.formState.errors.confirmPassword })} />
                                            <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground focus:outline-none" onClick={() => setIsConfirmVisible(!isConfirmVisible)} aria-label={isConfirmVisible ? 'Hide password' : 'Show password'}>
                                                {isConfirmVisible ? <EyeIcon className="h-4 w-4" /> : <EyeOffIcon className="h-4 w-4" />}
                                            </button>
                                        </div>
                                        {form.formState.errors.confirmPassword && <span className="text-sm text-destructive">{form.formState.errors.confirmPassword.message}</span>}
                                    </div>

                                    <Button type="submit" className="w-full" disabled={emailCheckMutation.isPending}>
                                        {emailCheckMutation.isPending ? 'Checking...' : 'Register'}
                                    </Button>

                                    <p className="text-center text-sm text-muted-foreground">
                                        Already have an account?
                                        {' '}
                                        <Link to="/login" className="text-primary font-medium hover:underline">Login</Link>
                                    </p>
                                </div>
                            </form>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
